import { ApiUrl } from "../Api/ApiUrl";
import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import "../Components/css/Gallery.css";

const GalleryComponent = () => {
    const [galleryimages, setgalleryImages] = useState([]);
    const [filteredImages, setFilteredImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedImage, setSelectedImage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // ✅ Fetch images on mount
    useEffect(() => {
        axios
            .get(`${ApiUrl}/get/gallery_images`)
            .then((response) => {
                const imageData = response?.data?.data || [];
                setgalleryImages(imageData);
                setFilteredImages(imageData);

                // Extract unique categories
                const uniqueCategories = [...new Set(imageData.map((img) => img.categoryname))];
                setCategories(uniqueCategories);
            })
            .catch((error) => {
                console.error("Error fetching gallery images:", error);
            });
    }, []);

    // ✅ Filter images based on selected category
    const handleCategoryFilter = (category) => {
        setSelectedCategory(category);
        if (category === "all") {
            setFilteredImages(galleryimages);
        } else {
            const filtered = galleryimages.filter((img) => img.categoryname === category);
            setFilteredImages(filtered);
        }
    };

    // ✅ Open/Close modal
    const openModal = (image) => {
        setSelectedImage(image);
        setIsModalOpen(true);
        document.body.style.overflow = "hidden";
    };

    const closeModal = useCallback(() => {
        setSelectedImage(null);
        setIsModalOpen(false);
        document.body.style.overflow = "unset";
    }, []);

    // ✅ Navigate images (memoized with useCallback)
    const navigateImage = useCallback(
        (direction) => {
            const currentIndex = filteredImages.findIndex((img) => img.id === selectedImage.id);
            let newIndex;

            if (direction === "next") {
                newIndex = (currentIndex + 1) % filteredImages.length;
            } else {
                newIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
            }

            setSelectedImage(filteredImages[newIndex]);
        },
        [filteredImages, selectedImage]
    );

    // ✅ Handle keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!isModalOpen) return;

            if (e.key === "Escape") {
                closeModal();
            } else if (e.key === "ArrowRight") {
                navigateImage("next");
            } else if (e.key === "ArrowLeft") {
                navigateImage("prev");
            }
        };

        document.addEventListener("keydown", handleKeyPress);
        return () => document.removeEventListener("keydown", handleKeyPress);
    }, [isModalOpen, closeModal, navigateImage]);

    // ✅ Clean up overflow on unmount
    useEffect(() => {
        return () => {
            document.body.style.overflow = "unset";
        };
    }, []);

    return (
        <div className="gallery-container">
            <h1 className="gallery-title">Gallery</h1>

            {/* Category Filter */}
            <div className="filter-container">
                <div className="filter-buttons">
                    <button
                        className={`filter-btn ${selectedCategory === "all" ? "active" : ""}`}
                        onClick={() => handleCategoryFilter("all")}
                    >
                        All ({galleryimages.length})
                    </button>
                    {categories.map((category) => {
                        const count = galleryimages.filter((img) => img.categoryname === category).length;
                        return (
                            <button
                                key={category}
                                className={`filter-btn ${selectedCategory === category ? "active" : ""}`}
                                onClick={() => handleCategoryFilter(category)}
                            >
                                {category} ({count})
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="scrollable-content mt-5">
                {galleryimages.length === 0 ? (
                    <div className="loading-container">
                        <p><b>Loading...</b></p>
                    </div>
                ) : filteredImages.length === 0 ? (
                    <div className="no-images-container">
                        <p><b>No images found in this category</b></p>
                    </div>
                ) : (
                    <div className="gallery-grid">
                        {filteredImages.map((image, index) => (
                            <div
                                key={image.id || index}
                                className="gallery-card"
                                onClick={() => openModal(image)}
                            >
                                <div className="image-container">
                                    <img
                                        src={image.image || ""}
                                        alt={image.title || `Gallery image ${index + 1}`}
                                        className="gallery-image"
                                        loading="lazy"
                                    />
                                    <div className="image-overlay"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && selectedImage && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeModal}>✕</button>

                        {filteredImages.length > 1 && (
                            <button className="modal-nav modal-prev" onClick={() => navigateImage("prev")}>
                                ❮
                            </button>
                        )}

                        <div className="modal-image-container">
                            <img
                                src={selectedImage.image || ""}
                                alt={selectedImage.title || "Gallery image"}
                                className="modal-image"
                            />
                        </div>

                        {filteredImages.length > 1 && (
                            <button className="modal-nav modal-next" onClick={() => navigateImage("next")}>
                                ❯
                            </button>
                        )}

                        <div className="modal-info">
                            <div className="modal-category">
                                <span className="category-tag">{selectedImage.categoryname}</span>
                            </div>
                            {selectedImage.title && (
                                <div className="modal-caption">
                                    <h3>{selectedImage.title}</h3>
                                </div>
                            )}
                            {selectedImage.date && (
                                <div className="modal-date">
                                    <small>{selectedImage.date}</small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalleryComponent;
